/**
 * Tracked Google Sheets manager (admin-only Settings section).
 * Lets admins register many Sheets per module, each attributed to a Google account,
 * with per-row Sync / Edit / Delete and live sync polling.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchTrackedSheets,
  createTrackedSheet,
  updateTrackedSheet,
  deleteTrackedSheet,
  syncTrackedSheet,
  fetchSyncJob,
  type TrackedSheet,
} from '../../utils/api';

const MODULE_OPTIONS = [
  { value: 'cashflow', label: 'CashFlow' },
];

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

function statusPill(status: TrackedSheet['last_sync_status']) {
  const map: Record<string, { bg: string; color: string; text: string }> = {
    success: { bg: '#dcfce7', color: '#166534', text: 'success' },
    error: { bg: '#fee2e2', color: '#991b1b', text: 'error' },
    running: { bg: '#dbeafe', color: '#1e40af', text: 'running' },
    pending: { bg: '#fef3c7', color: '#92400e', text: 'pending' },
    timeout: { bg: '#fee2e2', color: '#991b1b', text: 'timeout' },
  };
  const cfg = status ? map[status] : null;
  if (!cfg) {
    return (
      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
        never synced
      </span>
    );
  }
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
      }}
    >
      {cfg.text}
    </span>
  );
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString();
}

interface NewSheetForm {
  module_slug: string;
  label: string;
  sheet_url: string;
  google_account_email: string;
  notes: string;
}

const EMPTY_FORM: NewSheetForm = {
  module_slug: 'cashflow',
  label: '',
  sheet_url: '',
  google_account_email: '',
  notes: '',
};

export default function TrackedSheetsManager() {
  const [sheets, setSheets] = useState<TrackedSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<NewSheetForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<NewSheetForm>>({});
  const [syncing, setSyncing] = useState<Set<string>>(new Set());

  const pollHandles = useRef<Map<string, number>>(new Map());

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchTrackedSheets();
      setSheets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sheets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      // Cleanup any pending polls
      pollHandles.current.forEach((h) => window.clearTimeout(h));
      pollHandles.current.clear();
    };
  }, [load]);

  function clearSyncing(sheetId: string) {
    setSyncing((prev) => {
      const next = new Set(prev);
      next.delete(sheetId);
      return next;
    });
    const h = pollHandles.current.get(sheetId);
    if (h !== undefined) {
      window.clearTimeout(h);
      pollHandles.current.delete(sheetId);
    }
  }

  async function pollJob(sheetId: string, jobId: string, deadline: number) {
    try {
      const job = await fetchSyncJob(jobId);
      if (job.status === 'pending' || job.status === 'running') {
        if (Date.now() > deadline) {
          clearSyncing(sheetId);
          await load();
          return;
        }
        const handle = window.setTimeout(
          () => pollJob(sheetId, jobId, deadline),
          POLL_INTERVAL_MS,
        );
        pollHandles.current.set(sheetId, handle);
        return;
      }
      // Done (success/error/timeout). Refresh from server.
      clearSyncing(sheetId);
      await load();
    } catch {
      clearSyncing(sheetId);
      await load();
    }
  }

  async function handleSyncOne(sheetId: string) {
    setSyncing((prev) => new Set(prev).add(sheetId));
    try {
      const result = await syncTrackedSheet(sheetId);
      if (result.status === 'error') {
        clearSyncing(sheetId);
        await load();
        return;
      }
      pollJob(sheetId, result.job_id, Date.now() + POLL_TIMEOUT_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
      clearSyncing(sheetId);
    }
  }

  async function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (!form.label.trim() || !form.sheet_url.trim()) {
        setError('Label and Sheet URL are required.');
        return;
      }
      await createTrackedSheet({
        module_slug: form.module_slug,
        label: form.label.trim(),
        sheet_url: form.sheet_url.trim(),
        google_account_email: form.google_account_email.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      setForm(EMPTY_FORM);
      setAddOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEditSave(id: string) {
    setSubmitting(true);
    setError(null);
    try {
      await updateTrackedSheet(id, {
        label: editForm.label,
        sheet_url: editForm.sheet_url,
        google_account_email: editForm.google_account_email ?? null,
        notes: editForm.notes ?? null,
      });
      setEditingId(null);
      setEditForm({});
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(sheet: TrackedSheet) {
    if (
      !confirm(
        `Delete "${sheet.label}"? This also removes all cashflow rows synced from this sheet.`,
      )
    ) {
      return;
    }
    try {
      await deleteTrackedSheet(sheet.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  function startEdit(s: TrackedSheet) {
    setEditingId(s.id);
    setEditForm({
      label: s.label,
      sheet_url: s.sheet_url,
      google_account_email: s.google_account_email ?? '',
      notes: s.notes ?? '',
    });
  }

  return (
    <section className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="card-title" style={{ margin: 0 }}>Tracked Google Sheets</h2>
        <button
          className="button button-primary"
          onClick={() => setAddOpen((v) => !v)}
          style={{ fontSize: '0.875rem' }}
        >
          {addOpen ? 'Cancel' : '+ Add sheet'}
        </button>
      </div>
      <p className="card-text">
        Register Google Sheets as data sources. Each sheet is attributed to a Google account and a
        module. The CashFlow page syncs all sheets in the <code>cashflow</code> module when you click
        “Sync all sheets”. Per-sheet sync is also available below.
      </p>

      {error && (
        <p className="export-status error" style={{ fontSize: '0.85rem' }}>
          ⚠️ {error}
        </p>
      )}

      {addOpen && (
        <form
          onSubmit={handleAddSubmit}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            padding: 16,
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            marginTop: 12,
            background: '#fafafa',
          }}
        >
          <label style={{ gridColumn: 'span 1', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Module</span>
            <select
              value={form.module_slug}
              onChange={(e) => setForm({ ...form, module_slug: e.target.value })}
              style={inputStyle}
            >
              {MODULE_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ gridColumn: 'span 1', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Label *</span>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Household tracker"
              style={inputStyle}
              required
            />
          </label>
          <label style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Sheet URL *</span>
            <input
              type="url"
              value={form.sheet_url}
              onChange={(e) => setForm({ ...form, sheet_url: e.target.value })}
              placeholder="https://docs.google.com/spreadsheets/d/…"
              style={inputStyle}
              required
            />
          </label>
          <label style={{ gridColumn: 'span 1', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Google account email</span>
            <input
              type="email"
              value={form.google_account_email}
              onChange={(e) => setForm({ ...form, google_account_email: e.target.value })}
              placeholder="owner@gmail.com"
              style={inputStyle}
            />
          </label>
          <label style={{ gridColumn: 'span 1', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Notes</span>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="optional"
              style={inputStyle}
            />
          </label>
          <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
            <button type="submit" className="button button-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save sheet'}
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                setForm(EMPTY_FORM);
                setAddOpen(false);
              }}
              style={{ background: '#fff', border: '1px solid var(--color-border)' }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="loading-text" style={{ marginTop: 12 }}>Loading…</p>
      ) : sheets.length === 0 ? (
        <p style={{ marginTop: 12, color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
          No sheets registered yet. Click “+ Add sheet” to register your first Google Sheet.
        </p>
      ) : (
        <div style={{ marginTop: 16, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <th style={thStyle}>Label</th>
                <th style={thStyle}>Module</th>
                <th style={thStyle}>Google account</th>
                <th style={thStyle}>Last synced</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sheets.map((s) => {
                const isEditing = editingId === s.id;
                const isSyncing = syncing.has(s.id) || s.last_sync_status === 'running' || s.last_sync_status === 'pending';
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f1f1f1', verticalAlign: 'top' }}>
                    <td style={tdStyle}>
                      {isEditing ? (
                        <input
                          value={editForm.label ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                          style={{ ...inputStyle, width: '100%' }}
                        />
                      ) : (
                        <>
                          <div style={{ fontWeight: 600 }}>{s.label}</div>
                          {s.notes && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                              {s.notes}
                            </div>
                          )}
                        </>
                      )}
                      {isEditing && (
                        <input
                          value={editForm.sheet_url ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, sheet_url: e.target.value })}
                          style={{ ...inputStyle, width: '100%', marginTop: 4 }}
                          placeholder="Sheet URL"
                        />
                      )}
                      {!isEditing && (
                        <a
                          href={s.sheet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.75rem', color: 'var(--color-accent)', wordBreak: 'break-all' }}
                        >
                          {s.sheet_url}
                        </a>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <code style={{ fontSize: '0.75rem' }}>{s.module_slug}</code>
                    </td>
                    <td style={tdStyle}>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editForm.google_account_email ?? ''}
                          onChange={(e) =>
                            setEditForm({ ...editForm, google_account_email: e.target.value })
                          }
                          style={{ ...inputStyle, width: '100%' }}
                          placeholder="email@example.com"
                        />
                      ) : (
                        <span style={{ fontSize: '0.8rem' }}>{s.google_account_email ?? '—'}</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '0.8rem' }}>{formatRelative(s.last_synced_at)}</span>
                    </td>
                    <td style={tdStyle}>{statusPill(s.last_sync_status)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handleEditSave(s.id)}
                              disabled={submitting}
                              style={btnStyle('primary')}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingId(null);
                                setEditForm({});
                              }}
                              style={btnStyle()}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleSyncOne(s.id)}
                              disabled={isSyncing}
                              style={btnStyle('primary')}
                              title="Trigger n8n sync for this sheet"
                            >
                              {isSyncing ? '⏳' : '🔄'} Sync
                            </button>
                            <button onClick={() => startEdit(s)} style={btnStyle()}>
                              Edit
                            </button>
                            <button onClick={() => handleDelete(s)} style={btnStyle('danger')}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: '0.85rem',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 8px',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-secondary)',
  fontWeight: 700,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 8px',
  fontSize: '0.85rem',
};

function btnStyle(variant?: 'primary' | 'danger'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.75rem',
    cursor: 'pointer',
    border: '1px solid var(--color-border)',
    background: '#fff',
    color: 'var(--color-text-primary)',
  };
  if (variant === 'primary') {
    return { ...base, background: 'var(--color-accent)', color: '#fff', borderColor: 'transparent' };
  }
  if (variant === 'danger') {
    return { ...base, color: '#991b1b', borderColor: '#fecaca' };
  }
  return base;
}
